import { useState } from 'react'
import { Link } from 'react-router-dom'
import { usePortalAuth, type WorkspaceChoice } from '../auth/PortalAuth'
import { ApiError } from '../api/client'

export default function LoginPage() {
  const { login } = usePortalAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [choices, setChoices] = useState<WorkspaceChoice[] | null>(null)

  async function signIn(tenantSlug?: string) {
    setError(null)
    setBusy(true)

    try {
      setChoices(await login(email, password, tenantSlug))
    } catch (e) {
      // The API distinguishes a locked account from a wrong password, and the
      // difference matters to someone on their fifth attempt.
      setError(e instanceof ApiError ? e.message : 'Could not sign in. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    void signIn()
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="login-brand">
          <div className="brand-mark" style={{ width: 36, height: 36, fontSize: 19 }}>
            📦
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Fleey</div>
            <div className="brand-sub">Customer Portal</div>
          </div>
        </div>

        {error && <div className="banner banner-error">{error}</div>}

        <div className="stack" style={{ gap: 13 }}>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              className="input"
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <button className="btn btn-primary" type="submit" disabled={busy} style={{ padding: 10 }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {choices && choices.length > 0 && (
            <div className="stack" style={{ gap: 8 }}>
              <div className="page-sub">You have a login with more than one transporter. Which one?</div>
              {choices.map((c) => (
                <button key={c.slug} type="button" className="btn" disabled={busy} onClick={() => void signIn(c.slug)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}

          <Link to="/forgot-password" style={{ textAlign: 'center', fontSize: 13 }}>Forgot your password?</Link>
        </div>

        <div className="demo-creds">
          <div className="demo-row">
            <span>Demo account</span>
            <code>customer@fleey.demo</code>
          </div>
          <div className="demo-row">
            <span>Password</span>
            <code>Fleey@2026</code>
          </div>
          <button type="button" className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => { setEmail('customer@fleey.demo'); setPassword('Fleey@2026') }}>
            Use the demo account
          </button>
          <div style={{ marginTop: 8, color: 'var(--text-faint)' }}>
            Tracking a single consignment? You do not need an account — use the link the
            transporter sent you.
          </div>
        </div>
      </form>
    </div>
  )
}
